import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { companyService, countryService, stateService, districtService } from '../services/api';
import './Register.css';

function CompanyRegister() {
  const navigate = useNavigate();
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    companyName: '',
    code: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    location: '',
    address: '',
    description: '',
    countryId: '',
    stateId: '',
    districtId: ''
  });

  useEffect(() => {
    countryService.getCountries().then(setCountries).catch(() => setCountries([]));
  }, []);

  useEffect(() => {
    if (formData.countryId) {
      stateService.getStates().then((data) => setStates(data.filter(s => s.CountryId === parseInt(formData.countryId))))
        .catch(() => setStates([]));
    } else {
      setStates([]);
      setFormData(f => ({ ...f, stateId: '', districtId: '' }));
    }
  }, [formData.countryId]);

  useEffect(() => {
    if (formData.stateId) {
      districtService.getDistricts().then((data) => setDistricts(data.filter(d => d.StateId === parseInt(formData.stateId))))
        .catch(() => setDistricts([]));
    } else {
      setDistricts([]);
      setFormData(f => ({ ...f, districtId: '' }));
    }
  }, [formData.stateId]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    try {
      setLoading(true);
      const payload = {
        Id: 0,
        Name: formData.companyName.trim(),
        Code: formData.code.trim() || `COMP${Date.now().toString(36).toUpperCase().slice(-6)}`,
        Email: formData.email.trim(),
        MobileNo: formData.phone.trim(),
        Password: formData.password,
        Address: formData.address.trim(),
        Location: formData.location.trim(),
        Description: formData.description.trim(),
        CountryId: parseInt(formData.countryId) || 0,
        StateId: parseInt(formData.stateId) || 0,
        DistrictId: parseInt(formData.districtId) || 0
      };
      await companyService.saveCompany(payload);
      alert('Company registration successful! You can now login with your email and password.');
      navigate('/login');
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-page">
      <div className="register-container">
        <div className="register-header">
          <h1>Company Registration</h1>
          <p>Register your company and start recruiting top talent</p>
        </div>

        <form className="register-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="companyName">Company Name *</label>
            <input
              type="text"
              id="companyName"
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="phone">Phone Number *</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="password">Password *</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password *</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="countryId">Country *</label>
              <select
                id="countryId"
                name="countryId"
                value={formData.countryId}
                onChange={handleChange}
                required
              >
                <option value="">Select Country</option>
                {countries.map((c) => (
                  <option key={c.Id} value={c.Id}>{c.Name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="stateId">State *</label>
              <select
                id="stateId"
                name="stateId"
                value={formData.stateId}
                onChange={handleChange}
                required
                disabled={!formData.countryId}
              >
                <option value="">Select State</option>
                {states.map((s) => (
                  <option key={s.Id} value={s.Id}>{s.Name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="districtId">District *</label>
              <select
                id="districtId"
                name="districtId"
                value={formData.districtId}
                onChange={handleChange}
                required
                disabled={!formData.stateId}
              >
                <option value="">Select District</option>
                {districts.map((d) => (
                  <option key={d.Id} value={d.Id}>{d.Name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="location">Location / City *</label>
              <input
                type="text"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder="e.g., City or Area"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="address">Address *</label>
            <input
              type="text"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="Full address"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Company Description *</label>
            <textarea
              id="description"
              name="description"
              rows="4"
              value={formData.description}
              onChange={handleChange}
              placeholder="Brief description about your company..."
              required
            ></textarea>
          </div>

          {error && (
            <div className="error-message" style={{ color: '#dc3545', padding: '0.75rem', backgroundColor: '#f8d7da', borderRadius: '5px', marginBottom: '1rem' }}>
              {error}
            </div>
          )}

          <div className="form-checkbox">
            <label>
              <input type="checkbox" required />
              <span>I agree to the Terms and Conditions *</span>
            </label>
          </div>

          <button type="submit" className="register-btn" disabled={loading}>
            {loading ? 'Registering...' : 'Register'}
          </button>

          <div className="login-link">
            <p>Already have an account? <Link to="/login">Login here</Link></p>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CompanyRegister;

